// ── Österreich-Engine: gleitender Durchschnittspreis ────────────────────────
//
// Eigenständige Bewertungs-Engine für AT (Neuvermögen ab 1.3.2021), PARALLEL zur
// deutschen FIFO-Engine in fifo.js. fifo.js bleibt byte-genau unverändert.
//
// Rechtsmodell (BMF Österreich):
//   - Kostenbasis = gleitender Durchschnittspreis je Coin (seit 2023 vorgeschrieben).
//   - Krypto→Krypto-Tausch (inkl. Stablecoins) = KEINE Realisierung → Buchwert-
//     fortführung: Kostenbasis (und Altvermögen-Status) wandern auf den Coin, der
//     hereinkommt. Kein G/V-Event.
//   - Realisierung nur bei Krypto→Fiat (verkauf, oder swap mit Fiat-IN) und beim
//     Bezahlen mit Krypto (externer Abgang, soweit Erlös bekannt).
//   - Altvermögen (Anschaffung vor 1.3.2021): alte 1-Jahres-Frist. Neuvermögen:
//     keine Frist, jede Realisierung zählt.
//   - Staking/Mining/Lending: Zufluss zum Marktwert (eigenes Income-Konto, NICHT
//     Teil der 27,5 %-Schätzung). Der Marktwert wird zugleich Kostenbasis.
//
// Bewusste Vereinfachung ggü. fifo.js: der Durchschnitt ist portfolioweit PRO
// COIN (national), NICHT wallet-spezifisch. Wallet-Transfers sind pool-neutral
// (nur die Netzwerkgebühr mindert den Bestand). Determinismus (Invariant 7) über
// kanonischen Pre-Sort + denselben kausalen Komparator wie fifo.js.
//
// Event-Shape ist 1:1 kompatibel zu fifo.js-Ergebnissen (typ, datum, coin,
// coinOut, coinIn, menge, mengeOut, mengeIn, preis, effectiveErloes, gebuehr,
// ak, gv, hf, stpfl, kaufDatum, soldLots[], costBasisUnknown) — damit reportEngine,
// mergeTaxableEventsByDay und buildDisposalLotRows ohne Sonderpfad konsumieren.
// Zusätzlich: costBasisMethod:'average' und (auf soldLots) ein alt-Flag.

import { matchTransfers, normalizeTransactions } from './transferMatcher';
import { INCOME_TYPES } from './transactionTypes';
import { D, D_ZERO, D_EPSILON, Decimal } from './decimalMath';
import { buildCausalSort } from './fifo';
import { tsMillis } from './txFingerprint';
import { AT } from './taxRules';

// Fiat-Quote-Coins — Spiegel von fifo.js#FIFO_FIAT_QUOTES (bewusste Duplikation,
// damit fifo.js nicht angefasst werden muss). Eine Swap-Seite in dieser Menge ist
// echtes Fiat: coinIn=Fiat → Realisierung, coinOut=Fiat → Anschaffung (Kauf).
const FIAT_QUOTES = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);
const isFiat = (sym) => FIAT_QUOTES.has(String(sym || '').toUpperCase());

const ALT_CUTOFF = AT.altvermoegenCutoff;         // '2021-03-01'

// AT (KryptowährungsVO §2/§4 + BMF): Belohnungen fürs Einsetzen eigener Coins
// (PoS-Staking, Airdrops, Mining, Mint/Hardfork) sind am ZUFLUSS NICHT steuerbar
// → Anschaffungskosten = 0; der VOLLE Veräußerungserlös wird später mit 27,5 %
// erfasst. Laufende Einkünfte (Lending/Zinsen/Farming/Cashback) bleiben dagegen
// am Zufluss zum Tarif (Marktwert wird Basis) — siehe INCOME_TYPES-Verzweigung.
const AT_ZERO_BASIS_INCOME = new Set(['staking', 'airdrop', 'mining', 'mint']);

// Haltedauer in ganzen Tagen (floor, integer-exakt — vgl. Invariant-Doktrin).
function holdingDays(buyStr, sellStr) {
  if (!buyStr || !sellStr) return null;
  const b = new Date(buyStr), s = new Date(sellStr);
  if (isNaN(b.getTime()) || isNaN(s.getTime())) return null;
  return Math.floor((s - b) / 864e5);
}

// Altvermögen-Frist (analog DE-Spekulationsfrist, BGB §188 Abs. 2): innerhalb der
// Frist (am/vor Jahrestag) zählt die Realisierung; ab dem Folgetag außerhalb.
function withinAltFrist(buyStr, sellStr) {
  if (!buyStr || !sellStr) return false;
  const buy = new Date(buyStr);
  if (isNaN(buy.getTime())) return false;
  const ann = new Date(Date.UTC(buy.getUTCFullYear() + 1, buy.getUTCMonth(), buy.getUTCDate()));
  return String(sellStr).slice(0, 10) <= ann.toISOString().slice(0, 10);
}

const isAltAcquisition = (datum) => !!datum && String(datum).slice(0, 10) < ALT_CUTOFF;

// ── Pool ──────────────────────────────────────────────────────────────────
// pool[coin] = {
//   neu: { qty, cost, unknownQty, firstDatum },   // Neuvermögen, gleitender Schnitt
//   alt: [ { datum, qty, costPer, unknown } ],     // Altvermögen, FIFO (älteste zuerst)
// }
function makeCoin() {
  return {
    neu: { qty: D_ZERO, cost: D_ZERO, unknownQty: D_ZERO, firstDatum: null },
    alt: [],
  };
}

export function buildAustrianPool(txs) {
  const normalized = normalizeTransactions(txs);
  const pairs      = matchTransfers(normalized);
  const cmp        = buildCausalSort(txs);

  const matchedOutIds = new Map(pairs.map(p => [p.out._docId || String(p.out.id), p]));
  const matchedInIds  = new Map(pairs.map(p => [p.in._docId  || String(p.in.id),  p]));

  const pool    = {};
  const results = [];

  const ensure = (coin) => (pool[coin] || (pool[coin] = makeCoin()));

  // Anschaffung in den Pool buchen (Kauf, Income, Gap-Eingang, Fiat→Krypto-Swap-IN).
  const acquire = (coin, datum, qtyD, totalCostD, hasPrice) => {
    if (!coin || qtyD.lte(D_ZERO)) return;
    const P = ensure(coin);
    if (isAltAcquisition(datum)) {
      P.alt.push({
        datum,
        qty:     qtyD,
        costPer: hasPrice && qtyD.gt(D_ZERO) ? totalCostD.div(qtyD) : D_ZERO,
        unknown: !hasPrice,
      });
    } else {
      P.neu.qty  = P.neu.qty.plus(qtyD);
      P.neu.cost = P.neu.cost.plus(hasPrice ? totalCostD : D_ZERO);
      if (!hasPrice) P.neu.unknownQty = P.neu.unknownQty.plus(qtyD);
      if (!P.neu.firstDatum || (datum && datum < P.neu.firstDatum)) P.neu.firstDatum = datum;
    }
  };

  // Abgang aus dem Pool entnehmen → Chunks (Altvermögen FIFO zuerst, dann
  // Neuvermögen zum gleitenden Schnitt). Liefert auch den ungedeckten Rest.
  const consume = (coin, qtyD, sellStr) => {
    const P = ensure(coin);
    let remaining = qtyD;
    const chunks = [];

    for (const lot of P.alt) {
      if (remaining.lte(D_EPSILON)) break;
      if (lot.qty.lte(D_EPSILON)) continue;
      const take = Decimal.min(lot.qty, remaining);
      chunks.push({
        kind:        'alt',
        qty:         take,
        cost:        take.times(lot.costPer),
        kaufDatum:   lot.datum,
        holdingDays: holdingDays(lot.datum, sellStr),
        unknown:     lot.unknown,
      });
      lot.qty   = lot.qty.minus(take);
      remaining = remaining.minus(take);
    }

    // Neuvermögen: BEPREISTE Menge zuerst (gleitender Durchschnitt NUR aus den
    // bekannten Kosten), dann unbepreiste Menge als eigener costBasisUnknown-Chunk.
    // So verfälscht eine einzelne Datenlücke (unbepreister Zugang, z.B. Transfer
    // ohne countervalue) NICHT die Basis der bepreisten Coins — sonst würde der
    // ganze Verkauf als „Basis unvollständig" gelten, obwohl der Großteil bepreist
    // ist (DE-FIFO matcht ebenfalls bepreiste Lots zuerst).
    if (remaining.gt(D_EPSILON) && P.neu.qty.gt(D_EPSILON)) {
      const pricedQty = P.neu.qty.minus(P.neu.unknownQty);
      if (remaining.gt(D_EPSILON) && pricedQty.gt(D_EPSILON)) {
        const take = Decimal.min(remaining, pricedQty);
        const avg  = P.neu.cost.div(pricedQty);
        chunks.push({
          kind:        'neu',
          qty:         take,
          cost:        take.times(avg),
          kaufDatum:   P.neu.firstDatum,
          holdingDays: holdingDays(P.neu.firstDatum, sellStr),
          unknown:     false,
        });
        P.neu.qty  = P.neu.qty.minus(take);
        P.neu.cost = P.neu.cost.minus(take.times(avg));
        remaining  = remaining.minus(take);
      }
      if (remaining.gt(D_EPSILON) && P.neu.unknownQty.gt(D_EPSILON)) {
        const take = Decimal.min(remaining, P.neu.unknownQty);
        chunks.push({
          kind:        'neu',
          qty:         take,
          cost:        D_ZERO,
          kaufDatum:   P.neu.firstDatum,
          holdingDays: holdingDays(P.neu.firstDatum, sellStr),
          unknown:     true,
        });
        P.neu.qty        = P.neu.qty.minus(take);
        P.neu.unknownQty = P.neu.unknownQty.minus(take);
        remaining        = remaining.minus(take);
      }
      // Numerischen Rest sauber auf 0 ziehen (Invariant 5: Pool == Bestand).
      if (P.neu.qty.lte(D_EPSILON)) {
        P.neu.qty = D_ZERO; P.neu.cost = D_ZERO; P.neu.unknownQty = D_ZERO; P.neu.firstDatum = null;
      }
    }

    return { chunks, unmatched: remaining.gt(D_EPSILON) ? remaining : D_ZERO };
  };

  // soldLot-Pseudo-Lot je Chunk (Single-Lot-Format für buildDisposalLotRows).
  const soldLotOf = (ch) => ({
    datum:  ch.kaufDatum,
    stueck: ch.qty.gt(D_ZERO) ? ch.cost.div(ch.qty).toNumber() : 0,
    use:    ch.qty.toNumber(),
    alt:    ch.kind === 'alt',
  });

  // Realisierung (verkauf / swap-mit-Fiat-IN): Erlös proportional auf die Chunks
  // verteilen, je Chunk ein eigenes Event (Alt/Neu trennen → korrekte stpfl-Summe).
  const realise = (tx, coin, dispMengeD, grossTotalD, feeTotalD, makeEvent) => {
    const { chunks, unmatched } = consume(coin, dispMengeD, tx.datum);
    const denom = dispMengeD.gt(D_ZERO) ? dispMengeD : D_EPSILON;

    for (const ch of chunks) {
      const share     = ch.qty.div(denom);
      const grossChunk = grossTotalD.times(share);
      const feeChunk   = feeTotalD.times(share);
      const stpfl = ch.kind === 'neu'
        ? true
        : withinAltFrist(ch.kaufDatum, tx.datum);
      const gv = ch.unknown
        ? null
        : grossChunk.minus(feeChunk).minus(ch.cost).toDecimalPlaces(2).toNumber();
      results.push(makeEvent({
        chunk:    ch,
        gross:    grossChunk.toDecimalPlaces(2).toNumber(),
        fee:      feeChunk.toDecimalPlaces(2).toNumber(),
        ak:       ch.cost.toDecimalPlaces(2).toNumber(),
        gv,
        stpfl,
        hf:       ch.holdingDays,
        kaufDatum: ch.kaufDatum,
        costBasisUnknown: ch.unknown || undefined,
        soldLots: [soldLotOf(ch)],
        menge:    ch.qty.toNumber(),
        altvermoegen: ch.kind === 'alt',
      }));
    }

    // Ungedeckter Abgang → trotzdem als Realisierung listen (nachweispflichtig),
    // Anschaffung offen (Invariant: kein stiller Negativbestand, Pool floored bei 0).
    if (unmatched.gt(D_EPSILON)) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[austrianCost] Überabgang ${coin}: ${unmatched} ungedeckt bei ${tx.typ} (${tx.datum})`);
      }
      const share = unmatched.div(denom);
      results.push(makeEvent({
        chunk:    { kind: 'neu', qty: unmatched },
        gross:    grossTotalD.times(share).toDecimalPlaces(2).toNumber(),
        fee:      feeTotalD.times(share).toDecimalPlaces(2).toNumber(),
        ak:       0,
        gv:       null,
        stpfl:    true,
        hf:       null,
        kaufDatum: null,
        costBasisUnknown: true,
        soldLots: [],
        menge:    unmatched.toNumber(),
        altvermoegen: false,
      }));
    }
  };

  // Buchwertfortführung beim neutralen Krypto→Krypto-Tausch: Out-Coin entnehmen,
  // Kosten + Altvermögen-Status proportional (mengeIn/mengeOut) auf den In-Coin
  // übertragen. KEIN Realisierungs-Event.
  const carrySwap = (coinOut, mengeOutD, coinIn, mengeInD, datum) => {
    const { chunks, unmatched } = consume(coinOut, mengeOutD, datum);
    const Pin = ensure(coinIn);
    if (mengeOutD.lte(D_ZERO)) {
      acquire(coinIn, datum, mengeInD, D_ZERO, false);
      return;
    }
    const scale = mengeInD.div(mengeOutD);
    for (const ch of chunks) {
      const inQty = ch.qty.times(scale);
      if (inQty.lte(D_ZERO)) continue;
      if (ch.kind === 'alt') {
        // Originaldatum bleibt → Altvermögen-Status überlebt den Tausch.
        Pin.alt.push({
          datum:   ch.kaufDatum,
          qty:     inQty,
          costPer: ch.unknown ? D_ZERO : ch.cost.div(inQty),
          unknown: ch.unknown,
        });
      } else {
        Pin.neu.qty  = Pin.neu.qty.plus(inQty);
        Pin.neu.cost = Pin.neu.cost.plus(ch.unknown ? D_ZERO : ch.cost);
        if (ch.unknown) Pin.neu.unknownQty = Pin.neu.unknownQty.plus(inQty);
        if (!Pin.neu.firstDatum || (ch.kaufDatum && ch.kaufDatum < Pin.neu.firstDatum)) {
          Pin.neu.firstDatum = ch.kaufDatum || Pin.neu.firstDatum;
        }
      }
    }
    // Ungedeckter Out-Anteil → entsprechender In-Anteil ohne Basis (Pool floored).
    if (unmatched.gt(D_EPSILON)) {
      acquire(coinIn, datum, unmatched.times(scale), D_ZERO, false);
    }
  };

  // Aktueller National-Bestand eines Coins (Neuvermögen + offene Altvermögen-Lots).
  const poolQty = (coin) => {
    const P = pool[coin];
    if (!P) return D_ZERO;
    let q = P.neu.qty;
    for (const lot of P.alt) q = q.plus(lot.qty);
    return q;
  };

  const _transferDone = new Set();
  const processTransfer = (pair) => {
    const out = pair.out, inn = pair.in;
    const outId = out._docId || String(out.id);
    if (_transferDone.has(outId)) return;
    _transferDone.add(outId);
    const coin = out.coin || out.sym || inn.coin || inn.sym;
    if (!coin) return;
    const outMenge = D(out.menge);
    const inMenge  = D(inn.menge);
    const avail    = poolQty(coin);

    if (avail.gte(outMenge.minus(D_EPSILON))) {
      // Bestand deckt den Abgang → echter interner Wallet-Transfer: pool-neutral,
      // nur die Netzwerkgebühr (gesendet > angekommen) mindert den Bestand.
      const fee = outMenge.minus(inMenge);
      if (fee.gt(D_EPSILON)) consume(coin, fee, inn.datum);
    } else {
      // Pool deckt den Abgang NICHT → die Coins stammen aus nicht erfassten Daten
      // (z.B. extern/on-chain erhalten, Anschaffung liegt nicht in den TX vor).
      // Die Einzahlung IST hier die Anschaffung: ungedeckten Teil als Lot mit
      // preis/countervalue der Einzahlung anlegen (analog DE-Gap-Lot in fifo.js
      // _processTransfer) — sonst fehlt die Kostenbasis beim späteren Verkauf.
      const shortfall = inMenge.minus(avail.gt(D_ZERO) ? avail : D_ZERO);
      if (shortfall.gt(D_EPSILON)) {
        const totalIn = D(inn.preis).gt(D_ZERO)
          ? D(inn.preis)
          : D(inn.countervalue || 0).gt(D_ZERO) ? D(inn.countervalue) : D_ZERO;
        const hasPrice = inMenge.gt(D_ZERO) && totalIn.gt(D_ZERO);
        acquire(coin, inn.datum, shortfall, hasPrice ? totalIn.times(shortfall.div(inMenge)) : D_ZERO, hasPrice);
      }
    }
  };

  // Kanonischer Pre-Sort (Invariant 7) wie in fifo.js: stabil chronologisch,
  // dann kausaler Komparator. Garantiert deterministische Reihenfolge unabhängig
  // von der Firestore-Eingabereihenfolge.
  const canonicalCmp = (a, b) => {
    const ta = tsMillis(a), tb = tsMillis(b);
    if (ta !== tb) return ta - tb;
    const ida = a._docId || String(a.id || '');
    const idb = b._docId || String(b.id || '');
    return ida < idb ? -1 : ida > idb ? 1 : 0;
  };

  [...normalized]
    .sort(canonicalCmp)
    .sort(cmp)
    .forEach(tx => {
      const txId    = tx._docId || String(tx.id);
      const gebuehr = D(tx.gebuehr);
      if (tx.coin == null) tx.coin = tx.sym ?? tx.asset ?? null;

      // ── Kauf ────────────────────────────────────────────────────────────
      if (tx.typ === 'kauf') {
        const menge = D(tx.menge);
        const total = D(tx.preis).plus(gebuehr);
        acquire(tx.coin, tx.datum, menge, total, menge.gt(D_ZERO) && total.gt(D_ZERO));
        results.push({ ...tx, gv: null, costBasisMethod: 'average' });

      // ── Einkommen ────────────────────────────────────────────────────────
      } else if (INCOME_TYPES.has(tx.typ)) {
        const menge = D(tx.menge);
        if (AT_ZERO_BASIS_INCOME.has(tx.typ)) {
          // Staking/Airdrop/Mining/Mint: am Zufluss NICHT steuerbar. Anschaffungs-
          // kosten = 0 (BEKANNT, nicht „unbekannt") → voller Erlös bei Veräußerung
          // mit 27,5 %. KEIN incomeEUR-Zufluss.
          acquire(tx.coin, tx.datum, menge, D_ZERO, true);
          results.push({ ...tx, gv: null, costBasisMethod: 'average' });
        } else {
          // Laufende Einkünfte (Lending/Zinsen/Farming/Cashback): Zufluss zum
          // Marktwert (zum Tarif, eigenes Konto), Marktwert wird Anschaffungsbasis.
          const total = D(tx.preis).plus(gebuehr);
          const hasPrice = menge.gt(D_ZERO) && total.gt(D_ZERO);
          acquire(tx.coin, tx.datum, menge, total, hasPrice);
          results.push({
            ...tx,
            gv: null,
            costBasisMethod: 'average',
            incomeEUR: hasPrice ? D(tx.preis).toDecimalPlaces(2).toNumber() : 0,
          });
        }

      // ── Swap ────────────────────────────────────────────────────────────
      } else if (tx.typ === 'swap') {
        if (!tx.coinOut || !tx.coinIn) {
          results.push({ ...tx, gv: null, costBasisUnknown: true, incomplete: true, costBasisMethod: 'average' });
          return;
        }
        const coinInFiat  = isFiat(tx.coinIn);
        const coinOutFiat = isFiat(tx.coinOut);
        const mengeOut = D(tx.mengeOut);
        const mengeIn  = D(tx.mengeIn);

        if (coinOutFiat && !coinInFiat) {
          // Fiat→Krypto = Anschaffung (wie Kauf). Kosten = ausgegebenes Fiat + Gebühr.
          const total = mengeOut.plus(gebuehr);
          acquire(tx.coinIn, tx.datum, mengeIn, total, mengeIn.gt(D_ZERO) && total.gt(D_ZERO));
          results.push({ ...tx, gv: null, costBasisMethod: 'average' });

        } else if (coinInFiat && !coinOutFiat) {
          // Krypto→Fiat = Realisierung. Erlös = Fiat-Menge (mengeIn).
          realise(tx, tx.coinOut, mengeOut, mengeIn, gebuehr, ({ gross, fee, ak, gv, stpfl, hf, kaufDatum, costBasisUnknown, soldLots, menge, altvermoegen }) => {
            const share = mengeOut.gt(D_ZERO) ? D(menge).div(mengeOut) : D_ZERO;
            return {
              ...tx,
              mengeOut: menge,
              mengeIn:  mengeIn.times(share).toNumber(),
              preis:    gross,
              effectiveErloes: gross,
              gebuehr:  fee,
              ak, gv, stpfl, hf, kaufDatum, costBasisUnknown, soldLots, altvermoegen,
              costBasisMethod: 'average',
            };
          });

        } else {
          // Krypto→Krypto (inkl. Stablecoins) = neutral, Buchwertfortführung.
          carrySwap(tx.coinOut, mengeOut, tx.coinIn, mengeIn, tx.datum);
          results.push({ ...tx, gv: null, swapNeutral: true, costBasisMethod: 'average' });
        }

      // ── Verkauf ─────────────────────────────────────────────────────────
      } else if (tx.typ === 'verkauf') {
        realise(tx, tx.coin, D(tx.menge), D(tx.preis), gebuehr, ({ gross, fee, ak, gv, stpfl, hf, kaufDatum, costBasisUnknown, soldLots, menge, altvermoegen }) => ({
          ...tx,
          menge,
          preis:   gross,
          gebuehr: fee,
          ak, gv, stpfl, hf, kaufDatum, costBasisUnknown, soldLots, altvermoegen,
          costBasisMethod: 'average',
        }));

      // ── Auszahlung / Transfer ───────────────────────────────────────────
      } else if (tx.typ === 'auszahlung') {
        if (matchedOutIds.has(txId)) {
          processTransfer(matchedOutIds.get(txId));
          results.push({ ...tx, gv: null, costBasisMethod: 'average' });
        } else if (tx.externalTransfer) {
          // Externer Abgang ohne erfassten Erlös → Basis entnehmen, kein G/V.
          consume(tx.coin, D(tx.menge), tx.datum);
          results.push({ ...tx, gv: null, externalOutflow: true, costBasisMethod: 'average' });
        } else {
          results.push({ ...tx, gv: null, unmatchedWithdrawal: true, costBasisMethod: 'average' });
        }

      // ── Einzahlung ──────────────────────────────────────────────────────
      } else if (tx.typ === 'einzahlung') {
        if (matchedInIds.has(txId)) {
          processTransfer(matchedInIds.get(txId));
        } else {
          const menge = D(tx.menge);
          const total = D(tx.preis).gt(D_ZERO)
            ? D(tx.preis).plus(gebuehr)
            : D(tx.countervalue || 0).gt(D_ZERO)
              ? D(tx.countervalue).plus(gebuehr)
              : D_ZERO;
          acquire(tx.coin, tx.datum, menge, total, menge.gt(D_ZERO) && total.gt(D_ZERO));
        }
        results.push({ ...tx, gv: null, costBasisMethod: 'average' });

      // ── Verlust ─────────────────────────────────────────────────────────
      } else if (tx.typ === 'loss') {
        const { chunks } = consume(tx.coin, D(tx.menge), tx.datum);
        for (const ch of chunks) {
          const stpfl = ch.kind === 'neu' ? true : withinAltFrist(ch.kaufDatum, tx.datum);
          results.push({
            ...tx,
            menge:     ch.qty.toNumber(),
            ak:        ch.cost.toDecimalPlaces(2).toNumber(),
            kaufDatum: ch.kaufDatum,
            hf:        ch.holdingDays,
            stpfl,
            gv:        ch.unknown ? null : ch.cost.neg().toDecimalPlaces(2).toNumber(),
            costBasisUnknown: ch.unknown || undefined,
            soldLots:  [soldLotOf(ch)],
            altvermoegen: ch.kind === 'alt',
            costBasisMethod: 'average',
          });
        }

      // ── Burn / Gebühr (Coin-Fee) ────────────────────────────────────────
      } else if (tx.typ === 'burn' || tx.typ === 'gebuehr') {
        if (tx.coin && D(tx.menge).gt(D_EPSILON)) consume(tx.coin, D(tx.menge), tx.datum);
        results.push({ ...tx, gv: null, costBasisMethod: 'average' });

      // ── Futures PnL / Funding-Fee (bilanziell) ──────────────────────────
      } else if (tx.typ === 'futures_pnl' || tx.typ === 'funding_fee') {
        const pnl = D(tx.preis || 0);
        if (tx.coin) {
          const coinAmt = D(tx.menge).gt(D_ZERO) ? D(tx.menge) : pnl.abs();
          if (pnl.lt(D_ZERO) && coinAmt.gt(D_EPSILON)) {
            consume(tx.coin, coinAmt, tx.datum);
          } else if (pnl.gt(D_ZERO) && coinAmt.gt(D_EPSILON)) {
            acquire(tx.coin, tx.datum, coinAmt, pnl, true);
          }
        }
        results.push({
          ...tx, ak: 0, kaufDatum: tx.datum, hf: 0, stpfl: true,
          gv: pnl.toDecimalPlaces(2).toNumber(), costBasisMethod: 'average',
        });

      } else {
        results.push({ ...tx, gv: null, costBasisMethod: 'average' });
      }
    });

  return { results, pool };
}

/**
 * Wie fifo(): liefert nur den angereicherten results-Array (AT-Bewertung).
 * @param {Array} txs Rohe Transaktionen
 * @returns {Array}
 */
export function austrianEvents(txs) {
  return buildAustrianPool(txs || []).results;
}

// ── Offene Restbestände je Coin (Anschaffungskosten zum gleitenden Ø) ────────
// Analog calcPortfolio (lotSystem/FIFO), aber average-cost: leitet Menge +
// Anschaffungskosten der NOCH offenen Bestände direkt aus dem AT-Pool ab.
//   invested = Neuvermögen-Restkosten (gleitender Ø) + Σ Altvermögen-Lot-Kosten
//   menge    = Neuvermögen-Restmenge + Σ offene Altvermögen-Lots
// Unbepreiste Anteile (unknownQty / unknown-Lots) tragen 0 zur Kostenbasis bei —
// exakt wie FIFO calcPortfolio costBasisUnknown-Lots nicht in `invested` einfließen.
// Speist `stats.invested` im AT-Asset-Export (statt der FIFO-Nebenzahl). Pool als
// Input, damit der Aufrufer buildAustrianPool nur EINMAL ausführt (results+pool).
// @param {Record<string, ReturnType<typeof makeCoin>>} pool aus buildAustrianPool
// @returns {Record<string, { menge: number, invested: number }>}
export function austrianRemainingByCoin(pool) {
  // Schlüssel auf UPPERCASE normalisieren: pool ist (wie fifo.js) nach rohem
  // tx.coin gekeyt — der Aufrufer schlägt mit sym.toUpperCase() nach. Ohne
  // Normalisierung liefert die Suche bei gemischt geschriebenen Coins still 0.
  // Kollidierende Casing-Varianten werden summiert (gleicher Vermögenswert).
  const acc = {};
  for (const [coin, P] of Object.entries(pool || {})) {
    const sym = String(coin).toUpperCase();
    let qty  = P.neu.qty;
    let cost = P.neu.cost;                       // bereits Restkosten (gleitender Ø)
    for (const lot of P.alt) {
      qty  = qty.plus(lot.qty);
      cost = cost.plus(lot.qty.times(lot.costPer)); // unknown-Lot: costPer = 0
    }
    const cur = acc[sym] || (acc[sym] = { qty: D_ZERO, cost: D_ZERO });
    cur.qty  = cur.qty.plus(qty);
    cur.cost = cur.cost.plus(cost);
  }
  const out = {};
  for (const [sym, v] of Object.entries(acc)) {
    if (v.qty.gt(D_EPSILON)) out[sym] = { menge: v.qty.toNumber(), invested: v.cost.toNumber() };
  }
  return out;
}
